/**
 * AI-request history API (TMMAPP-0029 / TMMAPI-0063, disclosure corrected TMMAPP-0030) —
 * the person's own stored manifests (ids and counts) and, since TMMAPP-0044 / TMMAPI-0071, the
 * exact post-redaction `outboundPayload` (null on rows stored before it was kept). Since
 * TMMAPP-0047 / TMMAPI-0085 tmm-api states truncation (`outboundTruncated`, `outboundBytes`) and
 * why a payload is absent (`outboundPayloadAbsentReason`); the SPA infers neither.
 * `inputManifest` stays `unknown`; the SPA resolves recognised keys in `AiRequestDetail`.
 *
 * TMMAPP-0148 U1 (TMMAPI-0184 U3, MM-D-149): the owner of a virtual account pays for every assistant
 * session anchored to it, so this log holds members' calls too. An assistant row carries its
 * `session {kind, name?, virtualAccountName?}` and `askedBy {name, mine}`; another member's private
 * room (or own session) is served by charge, time, model and outcome only (`ASKED_BY_ANOTHER`).
 */
import { civilRangeToInstants } from '@/i18n/zonedCivil';
import { request } from './client';
import type { PageResponseAiRequestView as WirePage, AiRequestView as WireView } from './generated';

/**
 * Why no payload is stored: made before it was kept, still in flight, stopped before sending, or
 * removed because a contributor's data was erased (TMMAPI-0181 U6).
 */
export type AiPayloadAbsentReason = NonNullable<WireView['outboundPayloadAbsentReason']>;

const ABSENT_REASONS: readonly AiPayloadAbsentReason[] = [
  'LEGACY',
  'PENDING',
  'NOT_SENT',
  'REMOVED',
  'ASKED_BY_ANOTHER',
];

export type AiRequestSessionKind = 'PRIVATE' | 'SHARED' | 'BREAKOUT' | 'SUPPORT';

const SESSION_KINDS: readonly AiRequestSessionKind[] = ['PRIVATE', 'SHARED', 'BREAKOUT', 'SUPPORT'];

/** An assistant call's session (TMMAPI-0184 U3), while it exists. */
export interface AiRequestSession {
  /** null for a kind this build doesn't know. */
  kind: AiRequestSessionKind | null;
  /** Omitted on another member's private room or session. */
  name: string | null;
  /** The virtual account it is anchored to (its owner pays); null when anchored to none. */
  virtualAccountName: string | null;
}

export interface AiRequest {
  id: number;
  createdAt: string;
  feature: string;
  model: string;
  outcome: string;
  /**
   * What the person's AI wallet was debited for this request (TMMAPI-0160 U1, MM-D-140), in minor
   * units of the wallet's currency; null when nothing was charged. Never the provider's cost.
   */
  chargedMinor: number | null;
  /**
   * The wallet's currency for `chargedMinor` (TMMAPI-0162 U6). Null when the owner has no wallet;
   * the history then uses the meter's currency.
   */
  currency: string | null;
  /** Free JSON from the stored manifest (ids/counts) — never a domain type. */
  inputManifest: unknown;
  /** The exact request body sent to the model, unedited; null when the row predates it. */
  outboundPayload: string | null;
  /** tmm-api kept only the head of the body; `false` when there is no payload. */
  outboundTruncated: boolean;
  /** The original body's UTF-8 size; null exactly when there is no payload. */
  outboundBytes: number | null;
  /** Why there is no payload; null when one is present (or the reason is unrecognised). */
  outboundPayloadAbsentReason: AiPayloadAbsentReason | null;
  /** An assistant call's session; null on any other row, and once the session is deleted. */
  session: AiRequestSession | null;
  /** Who asked an assistant call; `name` is null once they are erased. Null on any other row. */
  askedBy: { name: string | null; mine: boolean } | null;
}

export interface AiRequestPage {
  items: AiRequest[];
  nextCursor: string | null;
}

function absentReason(v: WireView['outboundPayloadAbsentReason']): AiPayloadAbsentReason | null {
  return ABSENT_REASONS.find((reason) => reason === v) ?? null;
}

export function normalizeAiRequest(w: WireView): AiRequest {
  return {
    id: w.id,
    createdAt: w.createdAt,
    feature: w.feature,
    model: w.model ?? '',
    outcome: w.outcome,
    chargedMinor: w.chargedMinor ?? null,
    currency: w.currency || null,
    // Pass through untouched — no parsing, no redaction.
    inputManifest: w.inputManifest,
    // Verbatim — an empty string is no payload, never a claim that one exists.
    outboundPayload: w.outboundPayload || null,
    outboundTruncated: w.outboundTruncated,
    outboundBytes: w.outboundBytes,
    outboundPayloadAbsentReason: absentReason(w.outboundPayloadAbsentReason),
    session:
      w.session === undefined
        ? null
        : {
            kind: SESSION_KINDS.find((kind) => kind === w.session?.kind) ?? null,
            name: w.session.name ?? null,
            virtualAccountName: w.session.virtualAccountName ?? null,
          },
    askedBy: w.askedBy === undefined ? null : { name: w.askedBy.name, mine: w.askedBy.mine },
  };
}

export function normalizeAiRequestPage(w: WirePage): AiRequestPage {
  return {
    items: w.items.map(normalizeAiRequest),
    nextCursor: w.page.nextCursor,
  };
}

export const aiRequestsApi = {
  /** GET /v1/me/ai-requests */
  async list(query: {
    /** Civil `YYYY-MM-DD` bounds, inclusive; sent as date-times in `timeZone` (TMMAPP-0161). */
    from?: string;
    to?: string;
    /** The person's zone (`/me` timezone): the day `from` starts and `to` ends in. */
    timeZone: string;
    feature?: string;
    cursor?: string;
    limit?: number;
  }): Promise<AiRequestPage> {
    const { timeZone, ...rest } = query;
    const range = civilRangeToInstants(query, timeZone);
    return normalizeAiRequestPage(
      await request<WirePage>('GET', '/v1/me/ai-requests', {
        query: { ...rest, from: range.from, to: range.to },
      }),
    );
  },
};
